import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  FlaskConical,
  Microscope,
  ShieldCheck,
  Beaker,
  Droplets,
  FileCheck,
  Sparkles,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";

const PILLARS = [
  {
    icon: Beaker,
    title: "Professional-Grade Cleaning Products",
    body: "Professional-grade cleaning chemicals designed for commercial and industrial use — supported by Safety Data Sheets (SDS) and documented chemical management.",
    list: [
      "Concentrated formulations: less plastic, less waste",
      "Enzyme-active degreasers for food-grade environments",
      "EN 14476 / EN 13727 verified disinfectants for healthcare",
      "Professional chemistry supported by SDS documentation",
    ],
  },
  {
    icon: Microscope,
    title: "Advanced Material Protection",
    body: "Wrong chemistry doesn't just under-clean — it damages. Our team analyses your specific flooring, stones, metals, and fabrics, then applies the appropriate cleaning method and product to clean safely.",
    list: [
      "Natural stone, terrazzo, polished concrete protocols",
      "Stainless-steel passivation-safe cleaning",
      "Vinyl, LVT, and rubber-floor stripping & sealing",
      "Powder-coated and anodised metal surface care",
    ],
  },
  {
    icon: FileCheck,
    title: "COSHH Compliance",
    body: "Every product we use is fully logged with corresponding Safety Data Sheets (SDS). Your site receives a complete chemical register on day one and updated whenever a product changes.",
    list: [
      "On-site COSHH binder, kept current at all times",
      "Pre-shift PPE check and exposure logging",
      "Decant labelling discipline (no unmarked bottles)",
      "Annual audit and reduction review",
    ],
  },
  {
    icon: Droplets,
    title: "Surface Science Methodology",
    body: "We don't just spray and wipe — we apply a methodology built on pH balancing, contact-time discipline, and validated wiping consumables. Cleaning becomes measurable, not magical.",
    list: [
      "pH-tested chemistry per surface group",
      "Validated contact times for disinfection efficacy",
      "Colour-coded microfiber to prevent cross-contamination",
      "ATP / swab testing on request for critical sites",
    ],
  },
];

const STANDARDS = [];

export default function ExpertisePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="expertise-page" className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        {/* Hero - dark navy with flask icon */}
        <section className="relative bg-[#0E2A57] text-white pt-40 md:pt-48 pb-20 md:pb-28 overflow-hidden">
          <div className="relative container-ags">
            <Link
              to="/"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#BEE7F5] hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              Back to home
            </Link>

            <div className="grid md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-16 bg-[#2BB6E5]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                    09 — In-House Industrial & Hospital Product Expertise
                  </span>
                </div>
                <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.0] tracking-[-0.04em] text-white">
                  We don't believe in
                  <br />
                  <span className="italic-display font-normal text-[#BEE7F5]">
                    ordinary
                  </span>{" "}
                  solutions.
                </h1>
                <p className="mt-8 text-white/80 text-lg md:text-xl leading-relaxed max-w-2xl">
                  We use professional cleaning chemicals, approved procedures,
                  and documented chemical management to protect every surface
                  and maintain high cleaning standards.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <div className="relative w-44 h-44 bg-white/10 backdrop-blur-sm flex items-center justify-center">
                  <FlaskConical size={88} strokeWidth={1.1} className="text-[#BEE7F5]" />
                  <div className="absolute top-3 right-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                    Chemical Management
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="bg-white py-24 md:py-36">
          <div className="container-ags">
            <div className="max-w-3xl mb-14">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-16 bg-[#2BB6E5]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                  Four pillars of our expertise
                </span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                Chemistry, methodology, materials and compliance — under one family roof.
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5EBF2] border border-[#E5EBF2]">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <article
                    key={p.title}
                    data-testid={`expertise-pillar-${i}`}
                    className="bg-white p-8 md:p-10 hover:bg-[#F4F8FC] transition-colors flex flex-col gap-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span className="bg-[#0E2A57] text-[#BEE7F5] w-14 h-14 flex items-center justify-center">
                        <Icon size={26} strokeWidth={1.4} />
                      </span>
                      <span className="text-[11px] uppercase tracking-[0.22em] text-[#2BB6E5] font-semibold mt-2">
                        0{i + 1} / 04
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-[#0E2A57] tracking-tight">
                      {p.title}
                    </h3>
                    <p className="text-[#475569] text-base leading-relaxed">
                      {p.body}
                    </p>
                    <ul className="divide-y divide-[#E5EBF2] border-t border-[#E5EBF2]">
                      {p.list.map((li) => (
                        <li key={li} className="py-3 text-sm text-[#050B14] flex items-start gap-3">
                          <span className="text-[#2BB6E5] font-bold mt-0.5">·</span>
                          <span>{li}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process callout — surface assessment */}
        <section className="bg-white py-24 md:py-36">
          <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-5">
              <div className="aspect-square bg-[#0E2A57] flex items-center justify-center relative overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-30"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 30% 30%, rgba(43,182,229,0.4), transparent 55%)",
                  }}
                />
                <div className="relative grid grid-cols-2 gap-6 p-12 text-center">
                  <div>
                    <Sparkles size={36} className="text-[#BEE7F5] mx-auto" />
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                      Surface Test
                    </div>
                  </div>
                  <div>
                    <Beaker size={36} className="text-[#BEE7F5] mx-auto" />
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                      pH Match
                    </div>
                  </div>
                  <div>
                    <ShieldCheck size={36} className="text-[#BEE7F5] mx-auto" />
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                      SDS Log
                    </div>
                  </div>
                  <div>
                    <FileCheck size={36} className="text-[#BEE7F5] mx-auto" />
                    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#BEE7F5] font-medium">
                      Sign-Off
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Surface assessment — the AGS difference
              </span>
              <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-4xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                Before a chemical touches your premises, we test the surface.
              </h2>
              <p className="mt-6 text-[#475569] text-lg leading-relaxed">
                Most cleaning contracts skip this step — and that's why stones
                etch, stainless steel pits, and vinyl floors yellow over time.
                We map every key surface in your premises, test the chemistry
                in a discreet area first, and document what works.
              </p>
              <ol className="mt-8 space-y-4">
                {[
                  "Walk-through surface inventory — recorded with photos",
                  "Small-area chemistry test (24-hour observation)",
                  "Prescription locked into your site's COSHH binder",
                  "Annual review to update for any new finishes added",
                ].map((s, i) => (
                  <li key={s} className="flex items-start gap-4">
                    <span className="bg-[#0E2A57] text-white w-8 h-8 flex items-center justify-center font-heading font-black text-sm flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-[#050B14] text-base md:text-lg leading-relaxed pt-1">
                      {s}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#F4F8FC] py-16 md:py-24 border-t border-[#E5EBF2]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
              Speak to the expert
            </span>
            <h2 className="mt-4 font-heading font-extrabold text-4xl md:text-5xl leading-[1.05] tracking-[-0.04em] text-[#0E2A57]">
              Have a tricky surface or compliance question?
            </h2>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-2xl mx-auto">
              Send us a photo and a few details. Our team will review your
              enquiry and recommend a suitable cleaning solution — usually
              within one business day.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] px-10 py-5 font-semibold tracking-wide transition-colors"
              >
                Ask the Expert
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/#quote"
                className="inline-flex items-center gap-3 border-2 border-[#0E2A57] text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-10 py-5 font-semibold tracking-wide transition-all"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
